import { useStore } from '@mirai/data-sources';
import { dateFormat, useLocale } from '@mirai/locale';
import { Button, Calendar as CalendarBase, styles, Text, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { calcFromDate, calcToDate, calcTooltips, getOccupation } from './helpers';
import { DEVICE_XS_HEIGHT } from './RatesExplorer.constants';
import { L10N } from './RatesExplorer.l10n';
import * as style from './RatesExplorer.module.css';
import { BookingDates, Field } from '../';
import { getDataSource, getHotels } from '../../Finder/helpers';
import {
  CALENDAR_FORMAT as format,
  getAccommodationType,
  getCalendarCaption,
  ICON,
  MONTHS_IN_MOBILE,
  parseValue,
  useDeferredCallback,
  valueFormat,
} from '../../helpers';

const Calendar = ({
  captions,
  highlights = [],
  minNightsWithDeals,
  pricesInfo: { currency, guests: propGuests, nights, taxType } = {},
  rangeMaxDays,
  rangeMinDays,
  visible,
  visited,
  onChange,
  onNavigation,
  onPress,
  ...others
}) => {
  const { height, isMobile } = useDevice();
  const { translate } = useLocale();
  const deferCallback = useDeferredCallback();
  const {
    value: { locale, ...store },
  } = useStore();

  const [focus, setFocus] = useState();
  const [value, setValue] = useState(valueFormat(others.value));

  useEffect(() => {
    setValue(valueFormat(others.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [others.value]);

  const [from, to] = parseValue(value);

  const { config: { hotels: dataSource, lodgingDenomination: individual } = {}, hotel: { id } = {} } = store || {};

  const tagId = getDataSource(store)?.places?.options?.find(
    ({ id: ids = [], isHotel } = {}) => isHotel && ids[0] === id,
  )?.value;

  const accommodationType = getAccommodationType({
    dataSource,
    hotels: getHotels(getDataSource(store)?.placesSource, tagId),
    individual: individual && typeof individual === 'object' ? individual[id] : individual,
  });

  const { adults = 0, children = 0, guests = 0 } = getOccupation(propGuests);

  const handleChange = useCallback(
    (next, event) => {
      deferCallback(() => onChange && onChange(next, event));
    },
    [deferCallback, onChange],
  );

  const handleFocus = useCallback(
    (next) => {
      deferCallback(() => setFocus(next));
    },
    [deferCallback],
  );

  const handleNavigation = useCallback(
    (next) => {
      deferCallback(() => onNavigation && onNavigation(next));
    },
    [deferCallback, onNavigation],
  );

  return React.createElement(
    isMobile ? Field : Fragment,
    isMobile
      ? {
          caption: value.length ? getCalendarCaption(parseValue(value), translate, dateFormat, locale) : undefined,
          error: visited && (!from || !to),
          icon: ICON.CALENDAR,
          label: translate(L10N.LABEL_WHEN),
          placeholder: getCalendarCaption(undefined, translate, dateFormat, locale),
          visible,
          onPress,
          className: style.fieldCalendar,
          testId: `${others.testId}-field`,
        }
      : undefined,
    <>
      <CalendarBase
        {...{
          ...others,
          captions,
          format,
          from: calcFromDate(highlights),
          highlights,
          l10n: { actionLoadMore: translate(L10N.ACTION_LOAD_MORE_DATES), captionType: translate(L10N.LABEL_PRICE) },
          locale,
          months: isMobile ? MONTHS_IN_MOBILE : undefined,
          range: true,
          rangeMaxDays,
          rangeMinDays,
          to: calcToDate(highlights, rangeMaxDays),
          tooltips: calcTooltips({ focus, from, highlights, minNightsWithDeals, rangeMinDays, to, translate }),
          value,
          onChange: handleChange,
          onFocus: handleFocus,
          onNavigation: handleNavigation,
          className: styles(style.content, height <= DEVICE_XS_HEIGHT && style.calendarXS),
        }}
      />

      <View className={style.calendarInfo}>
        {!isMobile && <BookingDates {...{ focus, from, rangeMinDays, to }} />}

        {currency && nights && taxType ? (
          <Text small className={style.notification}>
            {translate(L10N.LABEL_INFO_PRICES_WITH_GUESTS, {
              accommodation: translate(L10N.LABEL_ACCOMMODATION_TYPE, { type: accommodationType }),
              currency,
              guests: translate(guests > 0 ? L10N.LABEL_OCCUPATION_TYPE_GUEST : L10N.LABEL_ADULTS_AND_CHILDREN, {
                adults,
                children,
                guests,
              }),
              nights,
              taxType,
            })}
          </Text>
        ) : null}
      </View>

      {isMobile && (
        <View row className={style.footer}>
          <BookingDates {...{ focus, from, rangeMinDays, to }} />

          <Button disabled={!from || !to} onPress={onPress} testId={`${others.testId}-button-next`}>
            {translate(L10N.ACTION_NEXT)}
          </Button>
        </View>
      )}
    </>,
  );
};

Calendar.displayName = 'Mirai:Core:RatesExplorer:Calendar';

Calendar.propTypes = {
  captions: PropTypes.shape({}),
  highlights: PropTypes.arrayOf(PropTypes.string),
  minNightsWithDeals: PropTypes.number,
  pricesInfo: PropTypes.shape({
    currency: PropTypes.string,
    guests: PropTypes.number,
    nights: PropTypes.number,
    taxType: PropTypes.number,
  }),
  rangeMaxDays: PropTypes.number,
  rangeMinDays: PropTypes.number,
  visible: PropTypes.bool,
  visited: PropTypes.bool,
  onChange: PropTypes.func,
  onNavigation: PropTypes.func,
  onPress: PropTypes.func,
};

export { Calendar };
